package one.healthium.domain.common

import kotlinx.serialization.DeserializationStrategy
import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.descriptors.buildClassSerialDescriptor
import kotlinx.serialization.encoding.CompositeDecoder
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlinx.serialization.encoding.decodeStructure
import kotlinx.serialization.json.JsonContentPolymorphicSerializer
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import one.primehealth.app.TargetPlatform
import one.primehealth.app.targetPlatform


class PlatformSerializers : JsonContentPolymorphicSerializer<String>(String::class) {
    override fun selectDeserializer(element: JsonElement): DeserializationStrategy<String> {
        return when (element) {
            is JsonObject -> PlatformValueSerializer
            is JsonPrimitive -> PrimitiveValueSerializer
            else -> error("Unexpected JSON element")
        }
    }
}

@Serializable
data class PlatformValue<T>(
    val android: T,
    val ios: T,
    val web: T,
) {
    val value: T = when (targetPlatform()) {
        TargetPlatform.Android -> android
        TargetPlatform.iOS -> ios
        TargetPlatform.Web -> web
    }
}

private object PrimitiveValueSerializer : KSerializer<String> {
    override val descriptor: SerialDescriptor =
        PrimitiveSerialDescriptor("message", PrimitiveKind.STRING)

    override fun deserialize(decoder: Decoder): String {
        return decoder.decodeString()
    }

    override fun serialize(encoder: Encoder, value: String) {
        encoder.encodeString(value)
    }
}

object PlatformValueSerializer : KSerializer<String> {
    override val descriptor: SerialDescriptor = buildClassSerialDescriptor("one.healthium.domain.common.PlatformValue") {
        element("android", PrimitiveSerialDescriptor("android", PrimitiveKind.STRING))
        element("ios", PrimitiveSerialDescriptor("ios", PrimitiveKind.STRING))
        element("web", PrimitiveSerialDescriptor("web", PrimitiveKind.STRING))
    }

    override fun deserialize(decoder: Decoder): String {
        return decoder.decodeStructure(descriptor) {
            var android: String? = null
            var ios: String? = null
            var web: String? = null
            while (true) {
                when (val index = decodeElementIndex(descriptor)) {
                    0 -> android = decodeStringElement(descriptor, 0)
                    1 -> ios = decodeStringElement(descriptor, 1)
                    2 -> web = decodeStringElement(descriptor, 1)
                    CompositeDecoder.DECODE_DONE -> break
                    else -> error("Unexpected index: $index")
                }
            }
            when (targetPlatform()) {
                TargetPlatform.Android -> android
                TargetPlatform.iOS -> ios
                TargetPlatform.Web -> web ?: android
            } ?: throw Exception("Unknown platform")
        }
    }

    override fun serialize(encoder: Encoder, value: String) {
        val androidValue = if (targetPlatform() == TargetPlatform.Android) value else ""
        val iosValue = if (targetPlatform() == TargetPlatform.iOS) value else ""
        val webValue = if (targetPlatform() == TargetPlatform.Web) value else ""
        encoder.beginStructure(descriptor).run {
            encodeStringElement(descriptor, 0, androidValue)
            encodeStringElement(descriptor, 1, iosValue)
            encodeStringElement(descriptor, 2, webValue)
            endStructure(descriptor)
        }
    }
}